import { createCampaignDetailApi, createImportBatchApi } from '@discovr/features';
import type { DeliverableInstance, RowMoney } from '@discovr/types';
import { formatIncompleteNote, formatTotalNote } from '../../../lib/formatMoney';
import type { CreatorsRow } from './types';

// Re-export canonical label formatter and registry from types package.
export { fmtDeliverableType, DELIVERABLE_REGISTRY } from '@discovr/types';

// Singleton factory for the inline pill-count queries in the main component.
export const unifiedApi = createCampaignDetailApi();

// Singleton factory for the staged import-review wizard's 8 endpoints.
export const importBatchApi = createImportBatchApi();

export function toNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Profile-link URL for a platform username. */
export function profileLinkFor(platform: string | undefined, username: string): string {
  if (!username) return '';
  const p = (platform ?? '').toLowerCase();
  if (p === 'instagram') return `https://www.instagram.com/${username}`;
  return `https://www.youtube.com/${username}`;
}

/** Reinstating an admin-level removal or the brand's own rejection needs
 *  admin level on the campaign (BE: ReinstateNeedsAdminLevel); admin/creator
 *  rejections only need write level. */
export function reinstateNeedsAdminLevel(status: string): boolean {
  return status === 'removed_by_admin' || status === 'rejected_by_brand';
}

/** Returns true for terminal statuses (rejected or soft-removed) where no
 *  chat action is possible. Admin can reinstate any of them to pending. */
export function isRejectedStatus(status: string): boolean {
  return (
    status === 'rejected_by_brand' ||
    status === 'rejected_by_creator' ||
    status === 'rejected_by_admin' ||
    status === 'removed_by_admin'
  );
}

/**
 * resolvePreSelectedDeliverable — find the deliverable already picked for an entry,
 * via Zustand commercialDrafts or persisted is_selected flag.
 */
export function resolvePreSelectedDeliverable(
  entry: CreatorsRow,
  breakdown: null | undefined,
): string {
  const deliverables = entry.deliverables;
  if (deliverables.length === 0) return '';
  const TIER_MATCH: Record<string, string> = {
    integrated:  'integrated',
    dedicated:   'dedicated',
    shorts:      'short',
    reel:        'reel',
    story:       'story',
    stories:     'stories',
    post:        'post',
    static:      'static_post',
    carousel:    'carousel',
    non_collab:  'non_collab',
    link:        'link',
  };
  if (breakdown) {
    for (const [tier, substr] of Object.entries(TIER_MATCH)) {
      if (Number((breakdown as Record<string, unknown>)[tier] ?? 0) <= 0) continue;
      const match = deliverables.find((d) => d.type.toLowerCase().includes(substr));
      if (match) return match.type;
    }
  }
  const persisted = deliverables.find((d) => d.is_selected);
  return persisted ? persisted.type : '';
}

/** Go's total over a row's selected lines: `money.selected_total`, null (shown "—") when a foreign line has no fresh rate.
 *  A row without `money` (public share view) shows Go's `estimated_budget`, an INR figure (wire 2a.1), unless the row is incomplete; nothing is summed here. */
export function rowSelectedTotal(row: {
  deliverables: DeliverableInstance[];
  money?: RowMoney;
  estimated_budget?: number | null;
  incomplete?: boolean;
  unconverted_lines?: number;
}): { total: number | null; note: string; incomplete: boolean } {
  if (row.incomplete) return { total: null, note: formatIncompleteNote(row.unconverted_lines ?? 0), incomplete: true };
  if (row.money) return { total: row.money.selected_total, note: row.money.selected_total == null ? '' : formatTotalNote(row.money), incomplete: false };
  const selected = (row.deliverables ?? []).filter((d) => d.is_selected);
  if (selected.length === 0) return { total: row.estimated_budget ?? 0, note: '', incomplete: false };
  if (row.estimated_budget != null) return { total: row.estimated_budget, note: '', incomplete: false };
  return { total: null, note: '', incomplete: false };
}
